import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Search,
  Users,
  TrendingUp,
  Repeat,
  Building2,
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Progress } from "./components/ui/progress";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Question data
const sections = [
  {
    id: "discovery",
    title: "Discovery Engine",
    subtitle: "How Customers Find Your Restaurant",
    icon: Search,
    color: "from-blue-500 to-blue-600",
    questions: [
      {
        id: "q1_google_profile",
        question: "How strong is your Google Business Profile visibility?",
        options: [
          { value: 1, label: "Not optimized / incomplete" },
          { value: 2, label: "Basic listing only" },
          { value: 3, label: "Optimized but not actively managed" },
          { value: 4, label: "Regular updates and photos" },
          { value: 5, label: "Strong ranking and regular customer interactions" }
        ]
      },
      {
        id: "q2_instagram_posting",
        question: "How consistently do you post on Instagram?",
        options: [
          { value: 1, label: "No activity" },
          { value: 2, label: "Less than 2 posts per month" },
          { value: 3, label: "1–2 posts per week" },
          { value: 4, label: "3–4 posts per week" },
          { value: 5, label: "Daily content + reels" }
        ]
      },
      {
        id: "q3_paid_ads",
        question: "Do you run paid ads to attract customers?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Tried once or twice" },
          { value: 3, label: "Occasionally" },
          { value: 4, label: "Regular campaigns" },
          { value: 5, label: "Structured paid ad strategy" }
        ]
      },
      {
        id: "q4_influencer_marketing",
        question: "Do influencers or creators promote your restaurant?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Occasionally" },
          { value: 4, label: "Regular influencer collaborations" },
          { value: 5, label: "Structured influencer strategy" }
        ]
      },
      {
        id: "q5_offline_visibility",
        question: "How strong is your local offline visibility?",
        options: [
          { value: 1, label: "Hard to notice from outside" },
          { value: 2, label: "Average visibility" },
          { value: 3, label: "Good signage" },
          { value: 4, label: "Attractive storefront" },
          { value: 5, label: "Landmark location / very noticeable" }
        ]
      },
      {
        id: "q6_aggregator_dependence",
        question: "What percentage of your sales come from aggregators?",
        options: [
          { value: 1, label: "More than 80%" },
          { value: 2, label: "60–80%" },
          { value: 3, label: "40–60%" },
          { value: 4, label: "20–40%" },
          { value: 5, label: "Less than 20%" }
        ]
      }
    ]
  },
  {
    id: "conversion",
    title: "Conversion Engine",
    subtitle: "Why Customers Choose You",
    icon: Users,
    color: "from-emerald-500 to-emerald-600",
    questions: [
      {
        id: "q7_store_frontage",
        question: "How attractive is your store frontage?",
        options: [
          { value: 1, label: "Poor / outdated" },
          { value: 2, label: "Below average" },
          { value: 3, label: "Average" },
          { value: 4, label: "Attractive" },
          { value: 5, label: "Highly appealing / Instagrammable" }
        ]
      },
      {
        id: "q8_google_rating",
        question: "What is your average Google rating?",
        options: [
          { value: 1, label: "Below 3.5" },
          { value: 2, label: "3.5 – 3.9" },
          { value: 3, label: "4.0 – 4.2" },
          { value: 4, label: "4.3 – 4.5" },
          { value: 5, label: "Above 4.5" }
        ]
      },
      {
        id: "q9_hero_products",
        question: "Do you have clear \"Hero Products\"?",
        options: [
          { value: 1, label: "No hero products" },
          { value: 2, label: "Few popular items but not defined" },
          { value: 3, label: "Some items known locally" },
          { value: 4, label: "Strong hero products" },
          { value: 5, label: "Signature items that drive traffic" }
        ]
      },
      {
        id: "q10_offers_compelling",
        question: "How compelling are your offers?",
        options: [
          { value: 1, label: "No structured offers" },
          { value: 2, label: "Random discounts" },
          { value: 3, label: "Occasional offers" },
          { value: 4, label: "Structured offers" },
          { value: 5, label: "Strong acquisition offer system" }
        ]
      },
      {
        id: "q11_staff_recommendations",
        question: "Do staff actively recommend items?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Sometimes" },
          { value: 4, label: "Frequently" },
          { value: 5, label: "Systematic upsell process" }
        ]
      },
      {
        id: "q12_service_time",
        question: "How fast is your service time?",
        options: [
          { value: 1, label: "Frequently slow" },
          { value: 2, label: "Often slow" },
          { value: 3, label: "Average" },
          { value: 4, label: "Mostly fast" },
          { value: 5, label: "Consistently fast" }
        ]
      }
    ]
  },
  {
    id: "revenue",
    title: "Revenue Engine",
    subtitle: "How Much Customers Spend",
    icon: TrendingUp,
    color: "from-amber-500 to-amber-600",
    questions: [
      {
        id: "q13_aov_trend",
        question: "What is your Average Order Value trend?",
        options: [
          { value: 1, label: "Declining" },
          { value: 2, label: "Flat" },
          { value: 3, label: "Slightly improving" },
          { value: 4, label: "Improving steadily" },
          { value: 5, label: "Strongly improving" }
        ]
      },
      {
        id: "q14_bundles_combos",
        question: "Do you offer bundles or combos?",
        options: [
          { value: 1, label: "None" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Few bundles" },
          { value: 4, label: "Multiple bundles" },
          { value: 5, label: "Structured bundle strategy" }
        ]
      },
      {
        id: "q15_menu_engineering",
        question: "Have you done menu engineering?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Basic understanding only" },
          { value: 3, label: "Done once" },
          { value: 4, label: "Done occasionally" },
          { value: 5, label: "Regular menu engineering" }
        ]
      },
      {
        id: "q16_premium_items",
        question: "Are premium items highlighted?",
        options: [
          { value: 1, label: "No" },
          { value: 2, label: "Poorly" },
          { value: 3, label: "Average" },
          { value: 4, label: "Good highlighting" },
          { value: 5, label: "Strong premium positioning" }
        ]
      },
      {
        id: "q17_staff_upsell",
        question: "Do staff upsell items?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Sometimes" },
          { value: 4, label: "Often" },
          { value: 5, label: "Systematic upsell training" }
        ]
      },
      {
        id: "q18_sku_profitability",
        question: "Do you track SKU profitability?",
        options: [
          { value: 1, label: "No tracking" },
          { value: 2, label: "Rough idea only" },
          { value: 3, label: "Partial tracking" },
          { value: 4, label: "Regular analysis" },
          { value: 5, label: "Detailed SKU profitability tracking" }
        ]
      }
    ]
  },
  {
    id: "retention",
    title: "Retention Engine",
    subtitle: "Why Customers Return",
    icon: Repeat,
    color: "from-purple-500 to-purple-600",
    questions: [
      {
        id: "q19_customer_data",
        question: "Do you collect customer data?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Sometimes" },
          { value: 4, label: "Frequently" },
          { value: 5, label: "Structured CRM database" }
        ]
      },
      {
        id: "q20_whatsapp_campaigns",
        question: "Do you run WhatsApp campaigns?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Occasionally" },
          { value: 4, label: "Monthly campaigns" },
          { value: 5, label: "Structured retention campaigns" }
        ]
      },
      {
        id: "q21_repeat_visitors",
        question: "What percentage of customers are repeat visitors?",
        options: [
          { value: 1, label: "Less than 10%" },
          { value: 2, label: "10–20%" },
          { value: 3, label: "20–30%" },
          { value: 4, label: "30–40%" },
          { value: 5, label: "More than 40%" }
        ]
      },
      {
        id: "q22_loyalty_programs",
        question: "Do you run loyalty programs?",
        options: [
          { value: 1, label: "None" },
          { value: 2, label: "Informal loyalty" },
          { value: 3, label: "Basic loyalty program" },
          { value: 4, label: "Structured loyalty system" },
          { value: 5, label: "Highly optimized loyalty program" }
        ]
      },
      {
        id: "q23_review_requests",
        question: "Do you ask customers for reviews?",
        options: [
          { value: 1, label: "Never" },
          { value: 2, label: "Rarely" },
          { value: 3, label: "Sometimes" },
          { value: 4, label: "Often" },
          { value: 5, label: "Systematic review collection" }
        ]
      },
      {
        id: "q24_repeat_visit_tracking",
        question: "Do you track repeat visit data?",
        options: [
          { value: 1, label: "Not tracked" },
          { value: 2, label: "Rough estimate" },
          { value: 3, label: "Partial tracking" },
          { value: 4, label: "Regular tracking" },
          { value: 5, label: "Data driven repeat visit system" }
        ]
      }
    ]
  }
];

// Thank you page component
const ThankYouPage = ({ submissionId, score, rating }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-8">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Manrope']">
            Thank You!
          </h1>
          <p className="text-slate-300 text-lg">
            Your Restaurant Growth Diagnostic has been submitted successfully.
          </p>
        </div>
        
        <Card className="bg-white/10 backdrop-blur border-white/20 text-white mb-8">
          <CardContent className="pt-6">
            <div className="text-6xl font-bold mb-2">{score}/120</div>
            <div className="text-xl text-emerald-400 font-semibold">{rating}</div>
          </CardContent>
        </Card>
        
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <Mail className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-white font-semibold text-lg mb-2">Check Your Inbox</h3>
          <p className="text-slate-400 text-sm">
            Your personalized Growth Scorecard PDF report is being generated and will be sent to your email shortly.
          </p>
        </div>
        
        <p className="text-slate-500 text-sm mt-8">
          Powered by Food Kindle Growth Diagnostics
        </p>
      </div>
    </div>
  );
};

// Main Questionnaire Component
export const Questionnaire = () => {
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-4 = sections, 5 = contact
  const [answers, setAnswers] = useState({});
  const [contactInfo, setContactInfo] = useState({
    restaurant_name: "",
    city: "",
    email: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  const totalSteps = sections.length + 2; // intro + sections + contact
  const progress = (currentStep / (totalSteps - 1)) * 100;

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: parseInt(value) }));
  };

  const goToNextStep = () => {
    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPrevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isSectionComplete = (sectionIndex) => {
    const section = sections[sectionIndex];
    return section.questions.every(q => answers[q.id] !== undefined);
  };

  const calculateSectionScore = (sectionIndex) => {
    const section = sections[sectionIndex];
    return section.questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
  };

  const calculateTotalScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };

  const handleSubmit = async () => {
    if (!contactInfo.restaurant_name || !contactInfo.city || !contactInfo.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...contactInfo,
        ...answers
      };
      
      const response = await axios.post(`${API}/public/submit-diagnostic`, payload);
      setResult({
        score: response.data.score,
        rating: response.data.rating
      });
      setSubmitted(true);
      toast.success("Assessment submitted successfully!");
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted && result) {
    return <ThankYouPage submissionId={result.id} score={result.score} rating={result.rating} />;
  }

  // Intro page
  if (currentStep === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        <Toaster position="top-center" richColors />
        
        {/* Header */}
        <header className="border-b border-white/10">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_report-builder-pro-4/artifacts/lejhtfu6_Untitled%20design.png" 
              alt="Food Kindle" 
              className="h-10 w-10 rounded-md"
            />
            <div>
              <h1 className="font-bold text-white font-['Manrope']">Food Kindle</h1>
              <p className="text-xs text-slate-400">Growth Diagnostics</p>
            </div>
          </div>
        </header>

        {/* Hero */}
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Manrope'] leading-tight">
            Restaurant Growth<br />
            <span className="bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent">
              Diagnostic™
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Discover what's holding your restaurant back. Get a personalized scorecard 
            across 4 critical growth engines in just 5 minutes.
          </p>
          
          <Button 
            size="lg" 
            onClick={goToNextStep}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-full"
            data-testid="start-assessment-btn"
          >
            Start Free Assessment
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>

          {/* Features */}
          <div className="grid md:grid-cols-4 gap-6 mt-16">
            {sections.map((section, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center mb-4 mx-auto`}>
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-1">{section.title}</h3>
                <p className="text-slate-400 text-sm">{section.subtitle}</p>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-slate-500 text-sm mb-4">What you'll receive:</p>
            <div className="flex flex-wrap justify-center gap-4 text-slate-400 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Overall Growth Score
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Section-wise Analysis
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                AI-Generated Insights
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                PDF Report via Email
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Contact form (last step)
  if (currentStep === sections.length + 1) {
    const totalScore = calculateTotalScore();
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        <Toaster position="top-center" richColors />
        
        {/* Header with progress */}
        <header className="border-b border-white/10 sticky top-0 bg-slate-900/95 backdrop-blur z-10">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_report-builder-pro-4/artifacts/lejhtfu6_Untitled%20design.png" 
                  alt="Food Kindle" 
                  className="h-8 w-8 rounded-md"
                />
                <span className="text-white font-semibold font-['Manrope']">Growth Diagnostic</span>
              </div>
              <span className="text-slate-400 text-sm">Final Step</span>
            </div>
            <Progress value={progress} className="h-1" />
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Score preview */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 mb-8">
            <CardContent className="pt-6 text-center">
              <p className="text-slate-400 text-sm mb-2">Your Preliminary Score</p>
              <div className="text-5xl font-bold text-white mb-2">{totalScore}/120</div>
              <div className="flex justify-center gap-4 mt-4">
                {sections.map((section, idx) => (
                  <div key={idx} className="text-center">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${section.color} flex items-center justify-center mx-auto mb-1`}>
                      <section.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-white font-semibold text-sm">{calculateSectionScore(idx)}/30</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contact form */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Building2 className="w-5 h-5" />
                Almost Done!
              </CardTitle>
              <CardDescription>
                Enter your details to receive your personalized Growth Scorecard PDF
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="restaurant_name">Restaurant Name *</Label>
                <Input
                  id="restaurant_name"
                  placeholder="e.g., The Great Indian Kitchen"
                  value={contactInfo.restaurant_name}
                  onChange={(e) => setContactInfo(prev => ({ ...prev, restaurant_name: e.target.value }))}
                  data-testid="input-restaurant-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="city"
                    placeholder="e.g., Mumbai"
                    value={contactInfo.city}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, city: e.target.value }))}
                    className="pl-10"
                    data-testid="input-city"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@restaurant.com"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10"
                    data-testid="input-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    placeholder="+91 98765 43210"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                    className="pl-10"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button 
              variant="ghost" 
              onClick={goToPrevStep}
              className="text-slate-400 hover:text-white"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={loading || !contactInfo.restaurant_name || !contactInfo.city || !contactInfo.email}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="submit-assessment-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  Get My Scorecard
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Question sections
  const sectionIndex = currentStep - 1;
  const currentSection = sections[sectionIndex];
  const SectionIcon = currentSection.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <Toaster position="top-center" richColors />
      
      {/* Header with progress */}
      <header className="border-b border-white/10 sticky top-0 bg-slate-900/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_report-builder-pro-4/artifacts/lejhtfu6_Untitled%20design.png" 
                alt="Food Kindle" 
                className="h-8 w-8 rounded-md"
              />
              <span className="text-white font-semibold font-['Manrope']">Growth Diagnostic</span>
            </div>
            <span className="text-slate-400 text-sm">Section {sectionIndex + 1} of {sections.length}</span>
          </div>
          <Progress value={progress} className="h-1" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Section header */}
        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${currentSection.color} flex items-center justify-center mx-auto mb-4`}>
            <SectionIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 font-['Manrope']">
            {currentSection.title}
          </h2>
          <p className="text-slate-400">{currentSection.subtitle}</p>
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {currentSection.questions.map((question, qIdx) => (
            <Card key={question.id} className="bg-white/5 backdrop-blur border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base font-medium flex items-start gap-3">
                  <span className="bg-white/10 rounded-full w-6 h-6 flex items-center justify-center text-sm shrink-0">
                    {qIdx + 1}
                  </span>
                  {question.question}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={answers[question.id]?.toString()}
                  onValueChange={(value) => handleAnswerChange(question.id, value)}
                  className="space-y-2"
                >
                  {question.options.map((option) => (
                    <div 
                      key={option.value}
                      className={`
                        flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer
                        ${answers[question.id] === option.value 
                          ? 'bg-emerald-500/20 border-emerald-500' 
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }
                      `}
                      onClick={() => handleAnswerChange(question.id, option.value)}
                    >
                      <RadioGroupItem 
                        value={option.value.toString()} 
                        id={`${question.id}-${option.value}`}
                        className="border-white/30 text-emerald-500"
                      />
                      <Label 
                        htmlFor={`${question.id}-${option.value}`}
                        className="text-slate-300 cursor-pointer flex-1 text-sm"
                      >
                        {option.label}
                      </Label>
                      <span className="text-slate-500 text-xs">{option.value}/5</span>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Section score */}
        <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Section Score</span>
            <span className="text-white font-semibold">{calculateSectionScore(sectionIndex)}/30</span>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button 
            variant="ghost" 
            onClick={goToPrevStep}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button 
            onClick={goToNextStep}
            disabled={!isSectionComplete(sectionIndex)}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="next-section-btn"
          >
            {currentStep === sections.length ? "Enter Details" : "Next Section"}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
